import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { useUploadStore } from './stores/upload';
import { Toaster } from './components/ui/sonner';
import { DuplicatePhotosDialog } from './components/upload/DuplicatePhotosDialog';

import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { UserManagementPage } from './pages/admin/UserManagementPage';
import { CategoryManagementPage } from './pages/admin/CategoryManagementPage';
import { AssetsPage } from './pages/assets/AssetsPage';
import { AssetDetailPage } from './pages/assets/AssetDetailPage';
import { AssetReviewPage } from './pages/assets/AssetReviewPage';
import { SearchPage } from './pages/assets/SearchPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ErrorPage } from './pages/ErrorPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { UserSyncEffect } from './components/UserSyncEffect';
import { ConnectionStatusOverlay } from './components/ConnectionStatusOverlay';

function AuthGate({ children }: { children: React.ReactNode }) {
  const isLoading = useAuthStore(s => s.isLoading);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <>{children}</>;
}

function DuplicatesOverlay() {
  const pendingDuplicates = useUploadStore(s => s.pendingDuplicates);
  const clearDuplicates = useUploadStore(s => s.clearDuplicates);

  if (pendingDuplicates.length === 0) return null;

  return (
    <DuplicatePhotosDialog
      duplicates={pendingDuplicates}
      open
      onOpenChange={(open) => { if (!open) clearDuplicates(); }}
    />
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthGate>
        <BrowserRouter>
          <UserSyncEffect />
          <ConnectionStatusOverlay />
          <DuplicatesOverlay />
          <Toaster />
          <Routes>
            {/* Auth routes - no layout */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/:token" element={<RegisterPage />} />

            {/* Protected routes - with AppLayout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DashboardPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <AppLayout>
                    <UserManagementPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <AppLayout>
                    <CategoryManagementPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Asset routes */}
            <Route
              path="/assets"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AssetsPage
                      showStats
                      pageTitle="All Assets"
                      pageDescription="Complete asset inventory"
                      breadcrumbs={[{ label: 'Assets' }]}
                    />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/inventory"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AssetsPage
                      defaultFilter={{ lifecycle_status: 'Inventoried' }}
                      pageTitle="Inventory"
                      pageDescription="Assets awaiting valuation"
                      breadcrumbs={[{ label: 'Assets', href: '/assets' }, { label: 'Inventory' }]}
                    />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/sale"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AssetsPage
                      defaultFilter={{ lifecycle_status: 'Listed for Sale' }}
                      pageTitle="For Sale"
                      pageDescription="Assets currently listed for sale"
                      breadcrumbs={[{ label: 'Assets', href: '/assets' }, { label: 'For Sale' }]}
                    />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/sold"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AssetsPage
                      defaultFilter={{ lifecycle_status: 'Sold' }}
                      pageTitle="Sold"
                      pageDescription="Assets that have been sold"
                      breadcrumbs={[{ label: 'Assets', href: '/assets' }, { label: 'Sold' }]}
                    />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/delivered"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AssetsPage
                      defaultFilter={{ lifecycle_status: 'Delivered' }}
                      pageTitle="Delivered"
                      pageDescription="Assets that have been delivered"
                      breadcrumbs={[{ label: 'Assets', href: '/assets' }, { label: 'Delivered' }]}
                    />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/review"
              element={
                <ProtectedRoute allowedRoles={['Admin', 'Editor']}>
                  <AppLayout>
                    <AssetReviewPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AssetDetailPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SearchPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Error routes */}
            <Route path="/error" element={<ErrorPage />} />

            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 catch-all - must be last */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthGate>
    </ErrorBoundary>
  );
}

export default App;
